import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function About() {
  const { t } = useTranslation('marketing');

  const values = [
    {
      id: 'science',
      title: t('about.values.scienceDriven.title'),
      description: t('about.values.scienceDriven.description'),
      icon: 'science'
    },
    {
      id: 'personalized',
      title: t('about.values.personalized.title'),
      description: t('about.values.personalized.description'),
      icon: 'person'
    },
    {
      id: 'transparent',
      title: t('about.values.transparent.title'),
      description: t('about.values.transparent.description'),
      icon: 'privacy_tip'
    },
    {
      id: 'inclusive',
      title: t('about.values.inclusive.title'),
      description: t('about.values.inclusive.description'),
      icon: 'diversity_3'
    }
  ];

  const stats = [
    { value: '500+', label: t('about.statistics.members') },
    { value: '15+', label: t('about.statistics.research') },
    { value: '98%', label: t('about.statistics.satisfaction') },
    { value: '50', label: t('about.statistics.studies') }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary to-primary-container py-20 px-margin-mobile">
        <div className="max-w-container-max mx-auto text-center">
          <h1 className="text-display-lg text-on-primary mb-6">{t('about.hero.title')}</h1>
          <p className="text-headline-md text-on-primary/90 max-w-2xl mx-auto">
            {t('about.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-section-gap px-margin-mobile bg-background">
        <div className="max-w-container-max mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-display-md text-on-background mb-6">{t('about.mission.heading')}</h2>
              <p className="text-body-md text-on-background/80 mb-4 leading-relaxed">
                {t('about.mission.paragraph1')}
              </p>
              <p className="text-body-md text-on-background/80 mb-4 leading-relaxed">
                {t('about.mission.paragraph2')}
              </p>
              <p className="text-body-md text-on-background/80 leading-relaxed">
                {t('about.mission.paragraph3')}
              </p>
            </div>

            <div className="aspect-square bg-gradient-to-br from-secondary to-primary rounded-2xl overflow-hidden">
              <img
                src="/images/about-mission.jpg"
                alt="Wellis Mission"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-section-gap px-margin-mobile bg-primary-container">
        <div className="max-w-container-max mx-auto">
          <h2 className="text-display-md text-on-primary-container mb-12 text-center">
            {t('about.values.heading')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div key={value.id}>
                <div className="mb-4 p-3 bg-accent/20 rounded-full w-fit">
                  <span className="material-symbols-outlined text-accent text-3xl">{value.icon}</span>
                </div>
                <h3 className="text-headline-md text-on-primary-container mb-2">{value.title}</h3>
                <p className="text-body-md text-on-primary-container/80">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-section-gap px-margin-mobile bg-primary text-on-primary">
        <div className="max-w-container-max mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <p className="text-display-md font-bold mb-2">{stat.value}</p>
                <p className="text-body-md opacity-80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-b from-primary-container to-background py-section-gap px-margin-mobile">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-display-md text-on-background mb-6">{t('about.cta.heading')}</h2>
          <p className="text-headline-md text-on-background/70 mb-12">
            {t('about.cta.description')}
          </p>

          <Link
            to="/contact"
            className="inline-block px-12 py-4 bg-accent text-on-accent rounded-full font-bold text-label-lg uppercase tracking-widest hover:opacity-90 transition-opacity"
          >
            {t('about.cta.button')}
          </Link>
        </div>
      </section>
    </div>
  );
}
